import React, { useEffect, useState } from "react";
import {
  FeatherCalendar,
  FeatherChevronDown,
  FeatherChevronLeft,
  FeatherChevronRight,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { Button } from "../../../ui/components/Button";
import {
  addMonths,
  createMonthGrid,
  DATE_RANGE_OPTIONS,
  DateRangePreset,
  DateRangeValue,
  formatDateForDisplay,
  formatDateForInputDisplay,
  formatMonthTitle,
  getPresetDateRange,
  isDateBetween,
  isSameDay,
  normalizeDateRange,
} from "../utils/dateRangeFilters";

interface DateRangeFilterProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [draftPreset, setDraftPreset] = useState<DateRangePreset>(value.preset);
  const [draftFrom, setDraftFrom] = useState<Date | null>(value.from);
  const [draftTo, setDraftTo] = useState<Date | null>(value.to);
  const [visibleMonth, setVisibleMonth] = useState(
    new Date(value.from.getFullYear(), value.from.getMonth(), 1),
  );

  useEffect(() => {
    if (!open) {
      setDraftPreset(value.preset);
      setDraftFrom(value.from);
      setDraftTo(value.to);
      setVisibleMonth(
        new Date(value.from.getFullYear(), value.from.getMonth(), 1),
      );
    }
  }, [open, value]);

  const handleQuickSelect = (preset: DateRangePreset) => {
    setDraftPreset(preset);

    if (preset === "custom") {
      setDraftFrom(null);
      setDraftTo(null);
      setVisibleMonth(new Date());
      return;
    }

    const nextRange = getPresetDateRange(preset);
    setDraftFrom(nextRange.from);
    setDraftTo(nextRange.to);
    if (preset !== "all_time") {
      setVisibleMonth(
        new Date(nextRange.from.getFullYear(), nextRange.from.getMonth(), 1),
      );
    }
    onChange(nextRange);
    setOpen(false);
  };

  const handleDateSelect = (date: Date) => {
    setDraftPreset("custom");

    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(date);
      setDraftTo(null);
      return;
    }

    const normalizedRange = normalizeDateRange(draftFrom, date);
    setDraftFrom(normalizedRange.from);
    setDraftTo(normalizedRange.to);
  };

  const handleReset = () => {
    const defaultRange = getPresetDateRange("last_30_days");
    setDraftPreset(defaultRange.preset);
    setDraftFrom(defaultRange.from);
    setDraftTo(defaultRange.to);
    setVisibleMonth(
      new Date(
        defaultRange.from.getFullYear(),
        defaultRange.from.getMonth(),
        1,
      ),
    );
  };

  const handleApply = () => {
    if (!draftFrom || !draftTo) return;

    onChange({
      preset: draftPreset,
      from: draftFrom,
      to: draftTo,
    });
    setOpen(false);
  };

  const canApply = Boolean(draftFrom && draftTo);
  const selectedOptionLabel = DATE_RANGE_OPTIONS.find(
    (option) => option.value === value.preset,
  )?.label;
  const triggerLabel =
    value.preset === "custom"
      ? `${formatDateForDisplay(value.from)} - ${formatDateForDisplay(value.to)}`
      : (selectedOptionLabel ?? "Select date range");
  const showCustomCalendar = draftPreset === "custom";

  return (
    <div className="flex items-center gap-2">
      <span className="whitespace-nowrap text-body font-body text-subtext-color">
        Filter by:
      </span>
      <SubframeCore.Popover.Root open={open} onOpenChange={setOpen}>
        <SubframeCore.Popover.Trigger asChild={true}>
          <Button
            variant="neutral-secondary"
            icon={<FeatherCalendar />}
            iconRight={<FeatherChevronDown />}
          >
            {triggerLabel}
          </Button>
        </SubframeCore.Popover.Trigger>
        <SubframeCore.Popover.Portal>
          <SubframeCore.Popover.Content
            side="bottom"
            align="start"
            sideOffset={4}
            asChild={true}
          >
            <div
              className={`flex flex-col items-start rounded-md border border-solid border-neutral-border bg-default-background shadow-lg ${
                showCustomCalendar ? "min-w-[576px]" : "min-w-52"
              }`}
            >
              <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border px-4 py-3">
                <span className="text-body-bold font-body-bold text-default-font">
                  {showCustomCalendar ? "Select dates" : "Filter by date"}
                </span>
                <Button
                  className={showCustomCalendar ? "" : "hidden"}
                  variant="neutral-secondary"
                  size="small"
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </div>

              <div className="flex w-full items-start">
                <QuickSelectPanel
                  selectedPreset={draftPreset}
                  onSelect={handleQuickSelect}
                  showBorder={showCustomCalendar}
                />

                {showCustomCalendar && (
                  <div className="flex flex-col items-start gap-4 px-6 py-4 grow">
                    <div className="flex w-full items-center gap-3">
                      <span className="text-body font-body text-subtext-color">
                        From
                      </span>
                      <DateDisplay value={draftFrom} />
                      <span className="text-body font-body text-subtext-color">
                        -
                      </span>
                      <span className="text-body font-body text-subtext-color">
                        To
                      </span>
                      <DateDisplay value={draftTo} />
                    </div>

                    <div className="flex w-full items-center justify-between">
                      <Button
                        variant="neutral-tertiary"
                        icon={<FeatherChevronLeft />}
                        onClick={() =>
                          setVisibleMonth(addMonths(visibleMonth, -1))
                        }
                      />
                      <span className="text-body-bold font-body-bold text-default-font">
                        {formatMonthTitle(visibleMonth)}
                      </span>
                      <Button
                        variant="neutral-tertiary"
                        icon={<FeatherChevronRight />}
                        onClick={() =>
                          setVisibleMonth(addMonths(visibleMonth, 1))
                        }
                      />
                    </div>

                    <WeekdayHeader />
                    <CalendarGrid
                      visibleMonth={visibleMonth}
                      from={draftFrom}
                      to={draftTo}
                      onSelectDate={handleDateSelect}
                    />
                  </div>
                )}
              </div>

              {showCustomCalendar && (
                <div className="flex w-full items-center justify-end border-t border-solid border-neutral-border px-4 py-3">
                  <Button size="small" onClick={handleApply} disabled={!canApply}>
                    Apply
                  </Button>
                </div>
              )}
            </div>
          </SubframeCore.Popover.Content>
        </SubframeCore.Popover.Portal>
      </SubframeCore.Popover.Root>
    </div>
  );
};

interface QuickSelectPanelProps {
  selectedPreset: DateRangePreset;
  onSelect: (preset: DateRangePreset) => void;
  showBorder: boolean;
}

const QuickSelectPanel: React.FC<QuickSelectPanelProps> = ({
  selectedPreset,
  onSelect,
  showBorder,
}) => {
  return (
    <div
      className={`flex w-44 flex-none flex-col items-start gap-2 bg-default-background px-4 py-4 ${
        showBorder ? "border-r border-solid border-neutral-border" : ""
      }`}
    >
      <span className="text-caption-bold font-caption-bold text-subtext-color">
        Quick select
      </span>
      <div className="flex w-full flex-col items-start gap-1">
        {DATE_RANGE_OPTIONS.map((option) => (
          <Button
            key={option.value}
            className="h-7 w-full flex-none justify-start"
            variant={
              selectedPreset === option.value
                ? "brand-primary"
                : "neutral-tertiary"
            }
            size="small"
            onClick={() => onSelect(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

const DateDisplay: React.FC<{ value: Date | null }> = ({ value }) => {
  return (
    <div className="flex items-center gap-2 rounded-md border border-solid border-neutral-border px-3 py-1.5 grow">
      <FeatherCalendar className="text-body font-body text-neutral-400" />
      <span
        className={`text-body font-body ${
          value ? "text-default-font" : "text-neutral-400"
        }`}
      >
        {formatDateForInputDisplay(value)}
      </span>
    </div>
  );
};

const WeekdayHeader: React.FC = () => {
  return (
    <div className="w-full items-start gap-1 grid grid-cols-7">
      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
        <span
          key={day}
          className="text-caption font-caption text-subtext-color text-center"
        >
          {day}
        </span>
      ))}
    </div>
  );
};

interface CalendarGridProps {
  visibleMonth: Date;
  from: Date | null;
  to: Date | null;
  onSelectDate: (date: Date) => void;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  visibleMonth,
  from,
  to,
  onSelectDate,
}) => {
  return (
    <div className="w-full items-start gap-1 grid grid-cols-7">
      {createMonthGrid(visibleMonth).map(({ date, isCurrentMonth }) => {
        const isStart = Boolean(from && isSameDay(date, from));
        const isEnd = Boolean(to && isSameDay(date, to));
        const isInRange = Boolean(from && to && isDateBetween(date, from, to));
        const isSelected = isStart || isEnd;

        return (
          <Button
            key={date.toISOString()}
            className={`${isCurrentMonth ? "" : "opacity-40"} ${
              isInRange && !isSelected ? "bg-brand-50" : ""
            }`}
            variant={isSelected ? "brand-primary" : "neutral-tertiary"}
            size="small"
            onClick={() => onSelectDate(date)}
          >
            {date.getDate()}
          </Button>
        );
      })}
    </div>
  );
};
